'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();

import type { StockFeatures, ColumnDef } from '@tanstack/react-table';
import { Badge } from '@workspace/ui/components/badge';
import { Button } from '@workspace/ui/components/button';
import { DataTable } from '@workspace/ui/components/custom/table';
import { FilePaycheckIcon } from '@workspace/ui/components/custom-icons/FilePaycheckIcon';
import { useNuqsTableState } from '@workspace/ui/hooks';
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE, formatAmount } from '@workspace/ui/lib';
import { format } from 'date-fns';
import { Clock, PlusIcon } from 'lucide-react';
import Link from 'next/link';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { RestrictionDialog } from '@/components/restrictions/restriction-dialog';
import { showBatchTransferProgressToast } from '@/components/batch-transfer/batch-transfer-progress-toast';
import {
  type FailedBatchTransfer,
  useFailedBatchTransfers,
  useRetryFailedBatchTransfer,
} from '@/hooks/use-batch-transfers';
import { useRestrictionGate } from '@/hooks/use-user-restriction';
import { getWalletBalance } from '@/hooks/use-wallet';
import type { Transaction } from './transactions-table';

interface QueuedPaymentsSectionProps {
  currencyCode?: string;
  enabled?: boolean;
  fromEntity?: boolean;
  onViewDetails?: (transaction: Transaction) => void;
  onQueuedPaymentsChange?: (queuedCurrencyCodes: string[]) => void;
}

function getTotalAmount(transfer: FailedBatchTransfer): number {
  return transfer.data.reduce((sum, item) => sum + item.amount, 0);
}

function getCurrencyCode(transfer: FailedBatchTransfer): string {
  return transfer.data[0]?.from_currency?.code ?? 'EUR';
}

function getAffordableQueuedPayments(
  transfers: FailedBatchTransfer[],
  wallets: Array<{ currency_id: number; available_balance: string }>
): FailedBatchTransfer[] {
  const remainingBalanceByCurrencyId = new Map(
    wallets.map((wallet) => [wallet.currency_id, Number(wallet.available_balance)])
  );

  const paymentsToRetry: FailedBatchTransfer[] = [];

  for (const transfer of transfers) {
    const currencyId = transfer.data[0]?.from_currency?.id;
    if (currencyId == null) continue;

    const transferAmount = getTotalAmount(transfer);
    const remainingBalance = remainingBalanceByCurrencyId.get(currencyId) ?? 0;
    if (remainingBalance < transferAmount) continue;

    paymentsToRetry.push(transfer);
    remainingBalanceByCurrencyId.set(currencyId, remainingBalance - transferAmount);
  }

  return paymentsToRetry;
}

function mapFailedBatchTransferToTransaction(transfer: FailedBatchTransfer): Transaction {
  const totalAmount = getTotalAmount(transfer);
  const currency = getCurrencyCode(transfer);
  const dateStr = transfer.created_at;

  return {
    id: `#${transfer.id}`,
    type: 'wallet-to-wallet',
    date: format(new Date(dateStr), 'dd/MM/yyyy HH:mm'),
    amount: `${formatAmount(totalAmount)} ${currency}`,
    balance: '',
    status: 'queued',
    wallet_id: 0,
    currency_id: transfer.data[0]?.from_currency?.id ?? 0,
    reason: transfer.reason,
    numericId: transfer.id,
    currency,
    from_currency: currency,
    to_currency: transfer.data[0]?.to_currency?.code ?? currency,
    to_currency_id: transfer.data[0]?.to_currency?.id ?? 0,
    created_at: dateStr,
    description: 'Batch transfer',
    provider: 'wallet',
    note: transfer.reason,
    fees: { percent: 0, value: 0 },
    tax: 0,
    tax_converted: 0,
    user_name: '',
    user_email: '',
    user_id: transfer.user_id,
    numericAmount: totalAmount,
    total_converted_amount: '',
    reference: '',
    date_of_request: dateStr,
    date_of_payment: null,
    date_of_declined: null,
    date_of_rejection: null,
    transfer_type: 'batch-transfer',
    is_incoming: false,
    numberOfRecipients: transfer.data.length,
    failedBatchTransferId: transfer.id,
  };
}

interface ColumnOptions {
  showDetails: (transfer: FailedBatchTransfer) => void;
}

const getColumns = ({
  showDetails,
}: ColumnOptions): ColumnDef<StockFeatures, FailedBatchTransfer>[] => [
  {
    accessorKey: 'id',
    header: 'TRANSACTION',
    cell: ({ row }) => (
      <div className="flex items-center gap-3">
        <div className="bg-grey-100 dark:bg-grey-800 flex h-12 w-12 items-center justify-center rounded-full">
          <FilePaycheckIcon className="text-grey-900 dark:text-grey-300 size-6" />
        </div>
        <div className="flex flex-col space-y-1">
          <div className="font-medium text-black dark:text-neutral-100">Batch transfer</div>
          <div className="text-grey-700 dark:text-grey-400 text-sm">
            {format(new Date(row.original.created_at), 'dd/MM/yyyy')}
          </div>
        </div>
      </div>
    ),
    size: 35,
  },
  {
    accessorKey: 'data',
    header: () => <div className="text-left">AMOUNT</div>,
    cell: ({ row }) => {
      const totalAmount = getTotalAmount(row.original);
      const currency = getCurrencyCode(row.original);
      return (
        <div className="text-grey-1000 text-left text-base font-normal dark:text-neutral-100">
          -{formatAmount(totalAmount)} {currency}
        </div>
      );
    },
    size: 15,
  },
  {
    accessorKey: 'fail_type',
    header: 'STATUS',
    cell: ({ row }) => (
      <div className="flex items-center justify-between align-middle">
        <div className="flex items-center gap-2">
          <Clock className="h-5 w-5 text-orange-500" />
          <Badge size="lg" variant="orange" text="Queued" />
        </div>
        <Button
          variant="secondary"
          className="w-[110px] text-sm font-medium"
          onClick={() => showDetails(row.original)}
        >
          View details
        </Button>
      </div>
    ),
    size: 35,
  },
];

const getMobileColumns = ({
  showDetails,
}: ColumnOptions): ColumnDef<StockFeatures, FailedBatchTransfer>[] => [
  {
    header: 'TRANSACTION',
    accessorKey: 'id',
    cell: ({ row }) => {
      const totalAmount = getTotalAmount(row.original);
      const currency = getCurrencyCode(row.original);
      return (
        <div className="flex w-full flex-col gap-2">
          <div
            className="flex w-full items-center justify-between"
            onClick={() => showDetails(row.original)}
          >
            <div className="flex-1 space-y-1">
              <div className="text-grey-1000 text-sm font-medium dark:text-neutral-100">
                Batch transfer
              </div>
              <div className="text-grey-600 dark:text-grey-400 text-xs">
                {format(new Date(row.original.created_at), 'dd/MM/yyyy')}
              </div>
            </div>
            <div className="flex flex-col items-end space-y-1">
              <div className="text-grey-1000 text-sm font-normal dark:text-neutral-100">
                -{formatAmount(totalAmount)} {currency}
              </div>
              <div className="flex items-center gap-1">
                <Clock className="h-4 w-4 text-orange-500" />
                <Badge variant="orange" text="Queued" className="text-xs" />
              </div>
            </div>
          </div>
        </div>
      );
    },
  },
];

export function QueuedPaymentsSection({
  currencyCode = 'EUR',
  enabled = true,
  fromEntity = false,
  onViewDetails,
  onQueuedPaymentsChange,
}: QueuedPaymentsSectionProps) {
  const { pagination, setPagination } = useNuqsTableState({
    parsers: {},
    paginationKeys: {
      page: 'queuedPaymentsPage',
      pageSize: 'queuedPaymentsPageSize',
    },
  });

  const { data: failedBatchTransfersResponse, isLoading } = useFailedBatchTransfers(
    {
      from_entity: fromEntity,
      page: pagination.pageIndex,
      per_page: pagination.pageSize,
    },
    enabled
  );
  const retryMutation = useRetryFailedBatchTransfer();
  const { guardNavigation, restrictionDialogProps } = useRestrictionGate();
  const hasTriedAutoRetryRef = useRef(false);
  const isAutoRetryInFlightRef = useRef(false);

  const queuedPayments = failedBatchTransfersResponse?.data ?? [];
  const hasQueuedPayments = queuedPayments.length > 0;
  const totalQueuedPayments = failedBatchTransfersResponse?.pagination?.total ?? 0;

  useEffect(() => {
    if (isLoading || totalQueuedPayments <= 0) return;

    const lastPage = Math.max(
      DEFAULT_PAGE_INDEX,
      Math.ceil(totalQueuedPayments / pagination.pageSize)
    );

    if (
      pagination.pageIndex <= lastPage &&
      !(queuedPayments.length === 0 && pagination.pageIndex > DEFAULT_PAGE_INDEX)
    ) {
      return;
    }

    setPagination((prev) => ({ ...prev, pageIndex: lastPage }));
  }, [
    isLoading,
    totalQueuedPayments,
    queuedPayments.length,
    pagination.pageIndex,
    pagination.pageSize,
    setPagination,
  ]);

  const queuedCurrencyCodes = useMemo(
    () =>
      Array.from(
        new Set(
          queuedPayments.flatMap((t) =>
            t.data.map((item) => item.from_currency?.code).filter(Boolean)
          )
        )
      ) as string[],
    [queuedPayments]
  );

  const prevCodesRef = useMemo(() => ({ current: '' }), []);

  useEffect(() => {
    const codesString = queuedCurrencyCodes.join(',');
    if (codesString !== prevCodesRef.current) {
      prevCodesRef.current = codesString;
      onQueuedPaymentsChange?.(queuedCurrencyCodes);
    }
  }, [queuedCurrencyCodes, onQueuedPaymentsChange, prevCodesRef]);

  const tryAutoRetry = useCallback(async () => {
    if (!hasQueuedPayments || hasTriedAutoRetryRef.current || isAutoRetryInFlightRef.current) {
      return;
    }

    isAutoRetryInFlightRef.current = true;

    try {
      let paymentsToRetry: FailedBatchTransfer[] = [];

      if (fromEntity) {
        paymentsToRetry = queuedPayments.filter((transfer) => getCurrencyCode(transfer) === 'EUR');
      } else {
        // Look up the balance for each queued currency directly by currency id (same source the
        // wallet page uses). This reflects admin/external top-ups and avoids the unscoped /wallets
        // list, which can omit the user's wallet for a given currency and hide affordable retries.
        const currencyIds = Array.from(
          new Set(
            queuedPayments
              .map((transfer) => transfer.data[0]?.from_currency?.id)
              .filter((id): id is number => id != null)
          )
        );

        const balanceResults = await Promise.all(
          currencyIds.map(async (currencyId) => {
            try {
              const balance = await getWalletBalance(currencyId);
              return { currency_id: currencyId, available_balance: balance.available_balance };
            } catch {
              return null;
            }
          })
        );

        const wallets = balanceResults.filter(
          (wallet): wallet is { currency_id: number; available_balance: string } => wallet !== null
        );
        if (wallets.length === 0) {
          return;
        }

        paymentsToRetry = getAffordableQueuedPayments(queuedPayments, wallets);
      }

      // Mark attempted even when nothing is affordable so we don't refetch in a loop.
      // visibilitychange clears this so admin/external top-ups can unlock a later retry.
      hasTriedAutoRetryRef.current = true;

      if (paymentsToRetry.length === 0) {
        return;
      }

      showBatchTransferProgressToast();

      for (const transfer of paymentsToRetry) {
        try {
          await retryMutation.mutateAsync(transfer.id);
        } catch (error) {
          console.error(`[QueuedPayments] Failed to retry batch transfer ${transfer.id}:`, error);
        }
      }
    } finally {
      isAutoRetryInFlightRef.current = false;
    }
  }, [fromEntity, hasQueuedPayments, queuedPayments, retryMutation]);

  useEffect(() => {
    if (!isLoading && hasQueuedPayments) {
      void tryAutoRetry();
    }
  }, [isLoading, hasQueuedPayments, tryAutoRetry]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;

      hasTriedAutoRetryRef.current = false;
      if (!isLoading && hasQueuedPayments) {
        void tryAutoRetry();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [hasQueuedPayments, isLoading, tryAutoRetry]);

  const handleShowDetails = useCallback(
    (transfer: FailedBatchTransfer) => {
      const transaction = mapFailedBatchTransferToTransaction(transfer);
      onViewDetails?.(transaction);
    },
    [onViewDetails]
  );

  const columnOptions: ColumnOptions = useMemo(
    () => ({ showDetails: handleShowDetails }),
    [handleShowDetails]
  );

  if (isLoading || !hasQueuedPayments) {
    return null;
  }

  return (
    <div className="mb-6 overflow-hidden rounded-2xl border-2 border-red-200 bg-white dark:border-red-800 dark:bg-grey-1000">
      <DataTable
        title="Queued payments"
        subTitle="These payments are currently queued due to insufficient funds, and will be processed once your available wallet balance is sufficient."
        className="rounded-none bg-white [&>div:first-child]:mb-5 dark:bg-grey-1000"
        compact
        data={queuedPayments}
        columns={getColumns(columnOptions)}
        mobileColumns={getMobileColumns(columnOptions)}
        showDetails={handleShowDetails}
        pagination={pagination}
        setPagination={setPagination}
        totalItems={failedBatchTransfersResponse?.pagination?.total ?? 0}
        defaultPageSize={DEFAULT_PAGE_SIZE}
        showExpandButton={false}
        customButton={
          fromEntity ? undefined : (
            <Link href={`/wallet/add?currency=${currencyCode}`} onClick={guardNavigation}>
              <Button className="flex items-center gap-2 text-base font-medium sm:text-sm">
                <PlusIcon className="h-5! w-5! stroke-2! sm:h-4! sm:w-4!" />
                Add funds
              </Button>
            </Link>
          )
        }
      />
      <RestrictionDialog {...restrictionDialogProps} />
    </div>
  );
}
